import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../apiHelpers';
import useFleetStore from '../../stores/useFleetStore';
import { buttonClass, IconButton } from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import { Switch } from '../../components/ui/Switch';
import { Field } from '../../components/ui/Field';
import { Dialog } from '../../components/ui/Dialog';
import { Alert } from '../../components/ui/Alert';
import FadeIn from '../../components/ui/FadeIn';
import { CATEGORY, alpha } from '../../components/ui/categoryColors';
import { Panel } from '../../components/ui/Panel';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { Skeleton } from '../../components/ui/Skeleton';
import { printSingleStandardLabel, printSingleSquareQRLabel, printSingleBothLabels } from '../../utils/printing-utils';

// Icons
const ActivateIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
    </svg>
);

const PrintIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1"/>
        <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1"/>
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
    </svg>
);

const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
    </svg>
);

const WrenchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M1 0L0 1l2.2 3.081a1 1 0 0 0 .815.419h.07a1 1 0 0 1 .708.293l2.675 2.675-2.617 2.654A3.003 3.003 0 0 0 0 13a3 3 0 1 0 5.878-.851l2.654-2.617.968.968-.305.914a1 1 0 0 0 .242 1.023l3.27 3.27a.997.997 0 0 0 1.414 0l1.586-1.586a.997.997 0 0 0 0-1.414l-3.27-3.27a1 1 0 0 0-1.023-.242l-.914.305-.968-.968 2.617-2.654A3.003 3.003 0 0 0 13 0a3 3 0 1 0-.851 5.878L9.495 8.53l-2.675-2.675a1 1 0 0 1-.293-.708v-.07a1 1 0 0 0-.419-.815zm9.646 10.646a.5.5 0 0 1 .708 0l2.914 2.914a.5.5 0 0 1-.708.708l-2.914-2.914a.5.5 0 0 1 0-.708M3 11l.471.242.529.026.287.445.445.287.026.529L5 13l-.242.471-.026.529-.445.287-.287.445-.529.026L3 15l-.471-.242L2 14.732l-.287-.445L1.268 14l-.026-.529L1 13l.242-.471.026-.529.445-.287.287-.445.529-.026z"/>
    </svg>
);

const ChipIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M5 0a.5.5 0 0 1 .5.5V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2A2.5 2.5 0 0 1 14 4.5h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14a2.5 2.5 0 0 1-2.5 2.5v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14A2.5 2.5 0 0 1 2 11.5H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2A2.5 2.5 0 0 1 4.5 2V.5A.5.5 0 0 1 5 0m-.5 3A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13h7a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 11.5 3zM5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5zM6.5 6a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5z"/>
    </svg>
);

// Gen 2 activation constants
const GEN2_HOST = '0000-0000-0000-0000.local';
const AES_KEY = 'F021-0001-0901-0001-F021-777777-8888-9999';
const SSID_LIST = 'Firefly_01,Firefly_02,Firefly_03,Firefly_04,Firefly_05,Firefly_06,Firefly_07,Firefly_08,Firefly_09,Firefly_10,Firefly_11,Firefly_12';

function DroneActions({ drone, serialNumber, showQRKey, onToggleQRKey, onDroneUpdated }) {
    const api = useApi();
    const drones = useFleetStore((state) => state.drones);
    const [menuOpen, setMenuOpen] = useState(false);

    // Hardware IDs modal state
    const [hardwareModalOpen, setHardwareModalOpen] = useState(false);

    // Fix Activation modal state
    const [fixModalOpen, setFixModalOpen] = useState(false);
    const [wrongSerialInput, setWrongSerialInput] = useState('');
    const [wrongSerialFormatted, setWrongSerialFormatted] = useState('');
    const [wrongSerialFull, setWrongSerialFull] = useState('');
    const [wrongSerialValid, setWrongSerialValid] = useState(false);
    const [fixActivateStatus, setFixActivateStatus] = useState(null); // 'pending' | 'ask_uncommission' | 'success' | null

    const noteActivatedSNumber = () => {
        const currentDate = new Date();
        const year = currentDate.getUTCFullYear().toString().slice(-2);
        const month = (currentDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = currentDate.getUTCDate().toString().padStart(2, '0');
        const formattedDate = year + month + day;
        const droneToUpdate = { condition: 'RTF', notes: `esp active ${formattedDate}` };
        return api.patch(`/fleet/${drone.serialnumber}`, {
            body: droneToUpdate,
            errorAlert: "Can't activate drone, please try again"
        });
    };

    // Update and validate the wrong serial number input
    // Reuses the same logic as HomeScreen's updateSerialGen2Go
    const updateWrongSerial = (serial) => {
        // Strip any existing dashes to get raw input, uppercase
        const raw = serial.replace(/-/g, '').toUpperCase();

        // Limit to 5 characters (format: X-XXXX where X is hex)
        const limited = raw.slice(0, 5);

        // Store raw input for the input field
        setWrongSerialInput(limited);

        // Only add dash if we have 2+ characters (format: X-XXXX)
        const formattedSerial = limited.length >= 2
            ? `${limited[0]}-${limited.slice(1)}`
            : limited;

        setWrongSerialFormatted(formattedSerial);

        // Only validate when all 5 characters are entered
        if (limited.length < 5) {
            setWrongSerialFull('');
            setWrongSerialValid(false);
            return;
        }

        // Build full serial with prefix (matching pattern: ends with 000X-XXXX)
        const fullSuffix = `000${formattedSerial}`;
        setWrongSerialFull(fullSuffix);

        // Validate: check if a drone exists with this serial suffix
        const matchedDrone = drones.find((d) =>
            d.serialnumber.endsWith(fullSuffix)
        );

        if (matchedDrone && matchedDrone.serialnumber !== drone.serialnumber) {
            // Found a different drone - this is a valid "wrong" serial
            setWrongSerialValid(true);
        } else {
            setWrongSerialValid(false);
        }
    };

    // Build fix activation URL: use wrong serial as host, correct serial as query param
    const getFixActivationUrl = useCallback(() => {
        if (!wrongSerialFull) return '';

        // Find the matched drone to get its full serial number for the hostname
        const matchedDrone = drones.find((d) =>
            d.serialnumber.endsWith(wrongSerialFull)
        );

        if (!matchedDrone) return '';

        // Use the wrong drone's serial as the .local hostname
        const wrongHost = `${matchedDrone.serialnumber}.local`;

        // Build params with the CORRECT serial number (the current drone)
        const params = new URLSearchParams();
        params.set('droneid', drone.serialnumber.toUpperCase());
        params.set('aeskey', AES_KEY);
        params.set('ssidsta', SSID_LIST);

        return `http://${wrongHost}/setparameters?${params.toString()}`;
    }, [wrongSerialFull, drones, drone.serialnumber]);

    // Handle fix activation button click
    const handleFixActivate = () => {
        const url = getFixActivationUrl();
        if (!url) return;

        // Open the fix activation URL
        window.open(url, '_blank');
        // Show confirmation buttons
        setFixActivateStatus('pending');
    };

    // Handle fix activation confirmation
    const handleConfirmFixActivation = async (success) => {
        if (success) {
            await noteActivatedSNumber();
            if (onDroneUpdated) onDroneUpdated();
            // Show uncommission prompt instead of closing
            setFixActivateStatus('ask_uncommission');
        } else {
            setFixActivateStatus(null);
        }
    };

    // Uncommission the wrong drone (set to NYC, clear activation notes)
    const uncommissionWrongDrone = () => {
        // Find the wrong drone's full serial number
        const matchedDrone = drones.find((d) =>
            d.serialnumber.endsWith(wrongSerialFull)
        );

        if (!matchedDrone) return;

        const currentDate = new Date();
        const year = currentDate.getUTCFullYear().toString().slice(-2);
        const month = (currentDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = currentDate.getUTCDate().toString().padStart(2, '0');
        const formattedDate = year + month + day;

        const droneToUpdate = {
            condition: 'NYC',
            notes: `deactivated ${formattedDate}`
        };

        api.patch(`/fleet/${matchedDrone.serialnumber}`, {
            body: droneToUpdate,
            errorAlert: "Can't uncommission drone, please try again"
        })
            .then(() => {
                setFixActivateStatus('success');
                // Close modal after brief delay to show success
                setTimeout(() => {
                    setFixModalOpen(false);
                    setFixActivateStatus(null);
                    setWrongSerialInput('');
                    setWrongSerialFormatted('');
                    setWrongSerialFull('');
                    setWrongSerialValid(false);
                }, 1000);
            })
            .catch(() => {});
    };

    // Skip uncommission and just close
    const skipUncommission = () => {
        setFixActivateStatus('success');
        setTimeout(() => {
            setFixModalOpen(false);
            setFixActivateStatus(null);
            setWrongSerialInput('');
            setWrongSerialFormatted('');
            setWrongSerialFull('');
            setWrongSerialValid(false);
        }, 1000);
    };

    // Reset fix modal state when it closes
    const openFixModal = () => {
        setFixModalOpen(true);
        setWrongSerialInput('');
        setWrongSerialFormatted('');
        setWrongSerialFull('');
        setWrongSerialValid(false);
        setFixActivateStatus(null);
    };

    const closeFixModal = () => {
        setFixModalOpen(false);
        setWrongSerialInput('');
        setWrongSerialFormatted('');
        setWrongSerialFull('');
        setWrongSerialValid(false);
        setFixActivateStatus(null);
    };

    const handlePrintLabel = () => {
        printSingleStandardLabel(drone);
    };

    const handlePrintQRLabel = async () => {
        await printSingleSquareQRLabel(drone);
    };

    const handlePrintBothLabels = async () => {
        await printSingleBothLabels(drone);
    };

    const [activateStatus, setActivateStatus] = useState(null); // 'success' | 'pending' | 'confirm_reactivate' | null
    const [reachability, setReachability] = useState(null); // 'checking' | 'reachable' | 'unreachable' | null

    // Check if drone is already activated
    const isAlreadyActivated = drone.notes && drone.notes.toLowerCase().includes('esp active');

    // Build activation URL
    const getActivationUrl = useCallback(() => {
        const params = new URLSearchParams();
        params.set('droneid', drone.serialnumber.toUpperCase());
        params.set('aeskey', AES_KEY);
        params.set('ssidsta', SSID_LIST);
        return `http://${GEN2_HOST}/setparameters?${params.toString()}`;
    }, [drone.serialnumber]);

    // Get the base URL for reachability check
    const getCheckUrl = useCallback(() => {
        return `http://${GEN2_HOST}/`;
    }, []);

    // Reusable function to check reachability
    const checkReachability = useCallback(() => {
        const checkUrl = getCheckUrl();
        setReachability('checking');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

        fetch(checkUrl, {
            method: 'GET',
            mode: 'no-cors',
            signal: controller.signal
        })
            .then(() => {
                clearTimeout(timeoutId);
                setReachability('reachable');
            })
            .catch(() => {
                clearTimeout(timeoutId);
                // Any error (timeout, network error, etc.) means unreachable
                setReachability('unreachable');
            });

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [getCheckUrl]);

    // Check reachability when menu opens
    useEffect(() => {
        if (!menuOpen) {
            setReachability(null);
            setActivateStatus(null);
            return;
        }

        checkReachability();
    }, [menuOpen, checkReachability]);

    const handleActivate = () => {
        // If already activated, ask for confirmation first
        if (isAlreadyActivated && activateStatus !== 'confirm_reactivate') {
            setActivateStatus('confirm_reactivate');
            return;
        }

        const url = getActivationUrl();
        if (!url) return;

        // Open the activation URL
        window.open(url, '_blank');
        // Show confirmation buttons instead of auto-marking
        setActivateStatus('pending');
    };

    const handleConfirmActivation = async (success) => {
        if (success) {
            await noteActivatedSNumber();
            setActivateStatus('success');
            if (onDroneUpdated) onDroneUpdated();
        } else {
            setActivateStatus(null);
        }
        setMenuOpen(false);
    };

    return (
        <>
            {/* Menu Toggle Button - Fixed top right like other pages */}
            <button
                onClick={() => setMenuOpen(true)}
                className="fixed top-2 md:top-14 right-4 bg-surface-container-highest p-2 rounded
                    transition-all duration-200
                    hover:shadow-[0_0_12px_rgba(255,204,51,0.3)] hover:text-primary"
                aria-label="Open actions menu"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                >
                    <path
                        fillRule="evenodd"
                        d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
                    />
                </svg>
            </button>

            {/* Backdrop overlay */}
            <div
                className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
                    menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
            />

            {/* Slide Menu */}
            <div
                className={`fixed top-0 right-0 h-full z-50 bg-gray-900/95 backdrop-blur-md
                    shadow-[-4px_0_24px_rgba(0,0,0,0.5)]
                    transition-transform duration-300 ease-out
                    ${menuOpen ? "translate-x-0" : "translate-x-full"}
                    w-72`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-outline-variant">
                    <span className="text-on-surface-variant text-xs uppercase tracking-wider">Actions</span>
                    <Tooltip label="Close menu"><IconButton label="Close menu" onClick={() => setMenuOpen(false)}>
                        <CloseIcon />
                    </IconButton></Tooltip>
                </div>

                {/* Menu Items */}
                <div className="p-4 space-y-3">
                    <FadeIn key={activateStatus === 'pending' ? 'pending' : activateStatus === 'confirm_reactivate' ? 'confirm' : reachability === 'unreachable' ? 'unreachable' : 'idle'}>
                    {activateStatus === 'pending' ? (
                        <Alert
                            tone="info"
                            title="Did the activation page load successfully?"
                            action={(
                                <>
                                    <button
                                        onClick={() => handleConfirmActivation(true)}
                                        className={buttonClass('filled', 'sm')}
                                    >
                                        Yes, it worked
                                    </button>
                                    <button
                                        onClick={() => handleConfirmActivation(false)}
                                        className={buttonClass('quiet', 'sm')}
                                    >
                                        No, failed
                                    </button>
                                </>
                            )}
                        />
                    ) : activateStatus === 'confirm_reactivate' ? (
                        <Alert
                            tone="warning"
                            title="Already Activated"
                            action={(
                                <>
                                    <button
                                        onClick={handleActivate}
                                        className={buttonClass('filled', 'sm')}
                                    >
                                        Yes, reactivate
                                    </button>
                                    <button
                                        onClick={() => setActivateStatus(null)}
                                        className={buttonClass('quiet', 'sm')}
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                        >
                            Are you sure you want to reactivate <span className="font-mono text-primary">{drone.serialnumber}</span>?
                        </Alert>
                    ) : reachability === 'unreachable' ? (
                        <div role="alert" className="w-full rounded-panel bg-red-500/10 p-4">
                            <div className="mb-3 flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-control bg-red-500/20 text-red-300">
                                    <ActivateIcon />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-on-surface">Activate</p>
                                    <p className="text-xs text-red-300">Device unreachable</p>
                                </div>
                            </div>
                            <div className="mt-3 flex gap-2">
                                <Tooltip label="Retry connection"><IconButton label="Retry connection" size="sm" onClick={checkReachability}>
                                    <RefreshIcon />
                                </IconButton></Tooltip>
                                <button
                                    onClick={handleActivate}
                                    className={`${buttonClass('tonal', 'sm')} flex-1`}
                                >
                                    Try anyway
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            style={activateStatus === 'success' ? { backgroundColor: alpha(CATEGORY.green.mark, 0.1) } : undefined}
                            onClick={handleActivate}
                            disabled={reachability === 'checking'}
                            aria-busy={reachability === 'checking' || undefined}
                            className={`w-full p-4 bg-surface-container-low rounded-panel
                                text-left transition-all duration-200
                                ${activateStatus === 'success' ? '' :
                                  reachability === 'checking' ? 'opacity-70 cursor-wait' :
                                  'hover:bg-primary/10'}`}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    style={activateStatus === 'success' ? { backgroundColor: alpha(CATEGORY.green.mark, 0.2), color: CATEGORY.green.ink.oklch } : undefined}
                                    className={`w-10 h-10 rounded flex items-center justify-center
                                    ${activateStatus === 'success' ? '' :
                                      reachability === 'checking' ? 'bg-surface-container text-on-surface-disabled' :
                                      'bg-primary/20 text-primary-ink'}`}>
                                    {reachability === 'checking' ? (
                                        <LogoSpinner size="inline" label="Testing device connection" />
                                    ) : activateStatus === 'success' ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                                        </svg>
                                    ) : (
                                        <ActivateIcon />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-white">
                                        {activateStatus === 'success' ? 'Activated!' : 'Activate'}
                                    </p>
                                    <p className="text-xs text-on-surface-variant">
                                        {reachability === 'checking' ? 'Testing device connection' :
                                         activateStatus === 'success' ? 'Serial number set' :
                                         isAlreadyActivated ? 'ESP already configured' : 'Set WiFi serial number'}
                                    </p>
                                </div>
                            </div>
                            {/* Reserved slot: matches the retry/try-anyway button row that
                                appears when unreachable, so the card height is stable across states.
                                During the probe, fill it with skeleton placeholders for a "loading" cue. */}
                            <div aria-hidden="true" className="mt-3 h-9 flex gap-2">
                                {reachability === 'checking' && (
                                    <>
                                        <Skeleton className="h-9 w-12" />
                                        <Skeleton className="h-9 flex-1" />
                                    </>
                                )}
                            </div>
                        </button>
                    )}
                    </FadeIn>

                    {/* Fix Activation Button */}
                    <button
                        onClick={() => {
                            setMenuOpen(false);
                            openFixModal();
                        }}
                        className="w-full flex items-center gap-3 p-4 bg-surface-container-low rounded border border-transparent
                            text-left transition-all duration-200
                            hover:border-primary hover:bg-primary/10
                            hover:shadow-[0_0_12px_rgba(255,204,51,0.2)]"
                    >
                        <div className="w-10 h-10 rounded bg-surface-container flex items-center justify-center text-on-surface">
                            <WrenchIcon />
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-white">Fix Activation</p>
                            <p className="text-xs text-on-surface-variant">Correct wrong serial assignment</p>
                        </div>
                    </button>

                    {/* View Hardware IDs Button */}
                    <button
                        onClick={() => {
                            setMenuOpen(false);
                            setHardwareModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 p-4 bg-surface-container-low rounded border border-transparent
                            text-left transition-all duration-200
                            hover:border-primary hover:bg-primary/10
                            hover:shadow-[0_0_12px_rgba(255,204,51,0.2)]"
                    >
                        <div className="w-10 h-10 rounded bg-surface-container flex items-center justify-center text-on-surface">
                            <ChipIcon />
                        </div>
                        <div className="flex-1">
                            <p className="font-semibold text-white">View Hardware IDs</p>
                            <p className="text-xs text-on-surface-variant">STM32 UID, ESP32 MAC, etc.</p>
                        </div>
                    </button>

                    {/* Print Labels Section */}
                    <div className="bg-surface-container-low rounded p-3">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded bg-surface-container flex items-center justify-center text-on-surface">
                                <PrintIcon />
                            </div>
                            <p className="font-semibold text-white">Print Labels</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    handlePrintLabel();
                                    setMenuOpen(false);
                                }}
                                className={`${buttonClass('filled', 'md')} flex-1`}
                            >
                                Label
                            </button>
                            <button
                                onClick={() => {
                                    handlePrintQRLabel();
                                    setMenuOpen(false);
                                }}
                                className={`${buttonClass('info', 'md')} flex-1`}
                            >
                                QR
                            </button>
                            <button
                                onClick={() => {
                                    handlePrintBothLabels();
                                    setMenuOpen(false);
                                }}
                                className={`${buttonClass('good', 'md')} flex-1`}
                            >
                                Both
                            </button>
                        </div>
                        <p className="text-xs text-on-surface-variant mt-2">Label = 1.5" | QR = 11.4mm square</p>
                    </div>

                    {/* View Settings Divider */}
                    <div className="border-t border-gray-700 my-2" />
                    <p className="text-xs text-on-surface-variant uppercase tracking-wider mb-2">View Settings</p>

                    {/* Show QR Key Toggle */}
                    <Switch
                        label="Show QR Key"
                        labelSide="start"
                        fill
                        checked={!!showQRKey}
                        onChange={onToggleQRKey}
                        className="p-3 bg-surface-container-low rounded"
                    />
                </div>

                {/* Drone Info at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-outline-variant">
                    <p className="text-xs text-on-surface-variant mb-1">Drone</p>
                    <p className="font-mono text-sm text-primary">{drone.serialnumber}</p>
                </div>
            </div>

            {/* Hardware IDs Modal */}
            <Dialog
                open={hardwareModalOpen}
                onClose={() => setHardwareModalOpen(false)}
                title="Hardware IDs"
                meta={drone.serialnumber}
                closeHint="Close modal"
                footer={(
                    <button
                        onClick={() => setHardwareModalOpen(false)}
                        className={buttonClass('quiet', 'md')}
                    >
                        Close
                    </button>
                )}
            >
                <div className="space-y-3">
                <Panel on="panel" padding="sm" label="STM32 UID (Flight Controller)">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">STM32 UID (Flight Controller)</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.fcid || <span className="text-on-surface-variant italic">Not recorded</span>}</p>
                </Panel>
                <Panel on="panel" padding="sm" label="ESP32 MAC (WiFi Module)">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">ESP32 MAC (WiFi Module)</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.rclinkprim || <span className="text-on-surface-variant italic">Not recorded</span>}</p>
                </Panel>
                {drone.escid && (
                    <Panel on="panel" padding="sm" label="ESC ID">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">ESC ID</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.escid}</p>
                    </Panel>
                )}
                {drone.gpsid && (
                    <Panel on="panel" padding="sm" label="GPS ID">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">GPS ID</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.gpsid}</p>
                    </Panel>
                )}
                {drone.ledid && (
                    <Panel on="panel" padding="sm" label="LED ID">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">LED ID</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.ledid}</p>
                    </Panel>
                )}
                {drone.frameid && (
                    <Panel on="panel" padding="sm" label="Frame ID">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">Frame ID</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.frameid}</p>
                    </Panel>
                )}
                <Panel on="panel" padding="sm" label="Generation">
                    <p className="m-0 text-xs text-on-surface-variant mb-1">Generation</p>
                    <p className="m-0 font-mono text-sm text-on-surface break-all">{drone.generation || 'Unknown'}</p>
                </Panel>
                </div>
            </Dialog>

            {/* Fix Activation Modal */}
            <Dialog
                open={fixModalOpen}
                onClose={closeFixModal}
                dirty={fixActivateStatus === null && wrongSerialInput !== ''}
                title="Fix Activation"
                meta="Correct wrong serial assignment"
                closeHint="Close modal"
                footer={fixActivateStatus !== 'pending' && fixActivateStatus !== 'ask_uncommission' && fixActivateStatus !== 'success' ? (
                    <>
                        <button
                            onClick={closeFixModal}
                            className={buttonClass('quiet', 'md')}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleFixActivate}
                            disabled={!wrongSerialValid}
                            className={buttonClass('filled', 'md')}
                        >
                            Fix Activation
                        </button>
                    </>
                ) : undefined}
            >
                <div className="space-y-4">
                    {fixActivateStatus === 'pending' ? (
                        <Alert
                            tone="info"
                            title="Did the activation page load successfully?"
                            action={(
                                <>
                                    <button
                                        onClick={() => handleConfirmFixActivation(true)}
                                        className={buttonClass('filled', 'sm')}
                                    >
                                        Yes, it worked
                                    </button>
                                    <button
                                        onClick={() => handleConfirmFixActivation(false)}
                                        className={buttonClass('quiet', 'sm')}
                                    >
                                        No, failed
                                    </button>
                                </>
                            )}
                        />
                    ) : fixActivateStatus === 'ask_uncommission' ? (
                        <div className="space-y-4">
                            <Alert tone="success">Activation Fixed!</Alert>

                            <Alert
                                tone="warning"
                                title="Uncommission incorrect serial?"
                                action={(
                                    <>
                                        <button
                                            onClick={uncommissionWrongDrone}
                                            className={buttonClass('danger', 'sm')}
                                        >
                                            Yes, uncommission
                                        </button>
                                        <button
                                            onClick={skipUncommission}
                                            className={buttonClass('quiet', 'sm')}
                                        >
                                            No, skip
                                        </button>
                                    </>
                                )}
                            >
                                <p className="m-0 mb-2">
                                    Only do this if <span className="font-mono text-amber-400">...{wrongSerialFull}</span> has not been assigned to another physical drone yet.
                                </p>
                                <p className="m-0">
                                    This will set that serial to "Not Yet Commissioned" and mark it as deactivated.
                                </p>
                            </Alert>
                        </div>
                    ) : fixActivateStatus === 'success' ? (
                        <Alert tone="success">Done!</Alert>
                    ) : (
                        <>
                            {/* Current drone info */}
                            <Panel on="panel" padding="sm" label="Correct serial">
                                <div className="flex flex-col items-center">
                                    <p className="m-0 text-xs text-on-surface-variant mb-1">Correct Serial (this drone)</p>
                                    <p className="m-0 font-mono text-primary">{drone.serialnumber}</p>
                                </div>
                            </Panel>

                            {/* Wrong serial input */}
                            <div>
                                <label className="block text-sm text-on-surface-variant mb-2">
                                    Enter Wrong Serial Number (last 5 digits)
                                </label>
                                <Field
                                    type="text"
                                    value={wrongSerialFormatted}
                                    onChange={(e) => updateWrongSerial(e.target.value)}
                                    placeholder="X-XXXX"
                                    size="lg"
                                    error={!wrongSerialValid && wrongSerialInput.length >= 5}
                                    className={`font-mono text-center text-lg ${wrongSerialValid ? 'keep-ring ring-2 ring-green-500/60 focus:ring-green-500/80' : ''}`}
                                    maxLength={6}
                                    autoFocus
                                />
                                {wrongSerialValid && (
                                    <p className="text-xs text-green-400 mt-1">
                                        Found drone with serial ending in {wrongSerialFull}
                                    </p>
                                )}
                                {!wrongSerialValid && wrongSerialInput.length >= 5 && (
                                    <p className="text-xs text-red-400 mt-1">
                                        No drone found with this serial number
                                    </p>
                                )}
                            </div>

                            {/* What will happen */}
                            {wrongSerialValid && (
                                <Panel on="panel" padding="sm" label="What will happen">
                                    <p className="m-0 text-xs text-on-surface-variant mb-2">This will:</p>
                                    <ul className="m-0 text-sm text-on-surface space-y-1">
                                        <li>• Connect to the ESP at <span className="font-mono text-amber-400">...{wrongSerialFull}.local</span></li>
                                        <li>• Reprogram it with <span className="font-mono text-primary">{drone.serialnumber}</span></li>
                                    </ul>
                                </Panel>
                            )}
                        </>
                    )}
                </div>
            </Dialog>
        </>
    );
}

export default DroneActions;
